import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../api";
import QRCode from "qrcode";

function BlanchisseriePrint() {
  const { id } = useParams();

  const [data, setData] = useState(null);
  const [qrCode, setQrCode] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // ==========================================================
  // CHARGER L'ENCAISSEMENT
  // ==========================================================

  useEffect(() => {
    const charger = async () => {
      try {
        const response = await api.get(
          `/blanchisserie/${id}`
        );

        setData(response.data);
      } catch (err) {
        console.error(
          "Erreur chargement impression blanchisserie:",
          err
        );

        setError(
          "Impossible de charger le reçu."
        );
      } finally {
        setLoading(false);
      }
    };

    charger();
  }, [id]);

  // ==========================================================
  // GÉNÉRER QR CODE
  // ==========================================================

  useEffect(() => {
    if (!data?.encaissement?.numero) return;

    const verificationUrl =
      `https://app.immalta.com/verification/blanchisserie/${data.encaissement.numero}`;

    QRCode.toDataURL(verificationUrl, {
      width: 180,
      margin: 1,
      errorCorrectionLevel: "M",
    })
      .then(setQrCode)
      .catch((err) => {
        console.error(
          "Erreur génération QR:",
          err
        );
      });
  }, [data]);

  // ==========================================================
  // FORMATAGE
  // ==========================================================

  const formatMoney = (montant) =>
    Number(montant).toLocaleString("fr-FR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const formatDate = (date) =>
    new Date(date).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  // ==========================================================
  // LANCER L'IMPRESSION AUTOMATIQUEMENT
  // ==========================================================

  useEffect(() => {
    if (!loading && data && qrCode) {
      const timer = setTimeout(() => {
        window.print();
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [loading, data, qrCode]);

  // ==========================================================
  // ÉTATS
  // ==========================================================

  if (loading) {
    return (
      <div className="print-loading">
        Préparation du reçu...
      </div>
    );
  }

  if (error || !data?.encaissement) {
    return (
      <div className="print-error">
        {error || "Encaissement introuvable."}
      </div>
    );
  }

  const encaissement = data.encaissement;
  const lignes = data.lignes || [];

  // ==========================================================
  // REÇU
  // ==========================================================

  return (
    <>
      <div className="print-page">

        <div className="receipt">

          {/* ================================================
              EN-TÊTE
          ================================================= */}

          <div className="header">

            <div className="logo">
              ALTA
            </div>

            <div className="company">
              IMMEUBLE
            </div>

            <div className="type">
              PRESTATIONS
            </div>

          </div>

          <div className="separator" />

          {/* ================================================
              INFORMATIONS
          ================================================= */}

          <div className="info">

            <div>
              <strong>Reçu :</strong>{" "}
              {encaissement.numero}
            </div>

            <div>
              <strong>Date :</strong>{" "}
              {formatDate(
                encaissement.date_encaissement
              )}
            </div>

            <div>
              <strong>Client :</strong>{" "}
              {encaissement.client_nom ||
                "Client de passage"}
            </div>

            <div>
              <strong>Encaissé par :</strong>{" "}
              {encaissement.encaisseur_nom ||
                "Utilisateur non identifié"}
            </div>

          </div>

          <div className="separator" />

          {/* ================================================
              PRESTATIONS
          ================================================= */}

          <table className="table">

            <thead>
              <tr>

                <th>
                  Désignation
                </th>

                <th className="right">
                  Qté
                </th>

                <th className="right">
                  P.U.
                </th>

                <th className="right">
                  Total
                </th>

              </tr>
            </thead>

            <tbody>

              {lignes.map((ligne) => (

                <tr key={ligne.id}>

                  <td>
                    {ligne.designation}
                  </td>

                  <td className="right">
                    {ligne.quantite}
                  </td>

                  <td className="right">
                    {formatMoney(
                      ligne.prix_unitaire
                    )}
                  </td>

                  <td className="right">
                    {formatMoney(
                      ligne.total
                    )}
                  </td>

                </tr>

              ))}

            </tbody>

          </table>

          <div className="separator" />

          {/* ================================================
              TOTAL
          ================================================= */}

          <div className="total">

            <span>
              TOTAL
            </span>

            <strong>
              {formatMoney(
                encaissement.montant
              )}{" "}
              {encaissement.devise}
            </strong>

          </div>

          {/* ================================================
              MODE DE PAIEMENT
          ================================================= */}

          <div className="payment">

            <strong>
              Paiement :
            </strong>{" "}

            {encaissement.mode_paiement}

          </div>

          {/* ================================================
              OBSERVATION
          ================================================= */}

          {encaissement.observation && (
            <div className="observation">

              <strong>
                Observation :
              </strong>{" "}

              {encaissement.observation}

            </div>
          )}

          {/* ================================================
              QR CODE — BAS À DROITE
          ================================================= */}

          {qrCode && (
            <div className="qr-section">

              <img
                src={qrCode}
                alt="QR Code de vérification"
                className="qr-code"
              />

              <div className="qr-text">
                Scanner pour vérifier
              </div>

            </div>
          )}

          {/* ================================================
              FOOTER
          ================================================= */}

          <div className="footer">

            <div>
              Merci pour votre confiance.
            </div>

            <div>
              Reçu d'encaissement  des prestations
            </div>

          </div>

        </div>

      </div>

      {/* ====================================================
          CSS THERMIQUE EPSON TM-T20III
      ===================================================== */}

      <style>{`

        @page {
          size: 80mm auto;
          margin: 0;
        }

        html,
        body {
          margin: 0;
          padding: 0;
          width: 80mm;
          background: #fff;
        }

        body {
          font-family:
            Arial,
            Helvetica,
            sans-serif;
          color: #000;
        }

        .print-page {
          width: 80mm;
          margin: 0;
          padding: 0;
          background: #fff;
        }

        .receipt {
          width: 72mm;
          margin: 0 auto;
          padding: 4mm 2mm;
          box-sizing: border-box;
          background: #fff;
          color: #000;
          font-size: 9px;
        }

        /* ==================================================
           HEADER
        ================================================== */

        .header {
          text-align: center;
        }

        .logo {
          font-size: 20px;
          font-weight: 800;
          letter-spacing: 2px;
        }

        .company {
          font-size: 10px;
          letter-spacing: 3px;
          margin-top: 1px;
        }

        .type {
          font-size: 13px;
          font-weight: 700;
          margin-top: 5px;
        }

        /* ==================================================
           SEPARATOR
        ================================================== */

        .separator {
          border-top: 1px dashed #000;
          margin: 7px 0;
          height: 0;
        }

        /* ==================================================
           INFORMATIONS
        ================================================== */

        .info {
          font-size: 9px;
          line-height: 1.55;
        }

        /* ==================================================
           TABLE
        ================================================== */

        .table {
          width: 100%;
          border-collapse: collapse;
          table-layout: fixed;
          font-size: 8px;
        }

        .table th {
          padding: 3px 1px;
          border-bottom: 1px solid #000;
          font-weight: 700;
          text-align: left;
        }

        .table td {
          padding: 4px 1px;
          border-bottom: 1px dotted #999;
          vertical-align: top;
          word-wrap: break-word;
        }

        .table th:first-child,
        .table td:first-child {
          width: 43%;
        }

        .table th:nth-child(2),
        .table td:nth-child(2) {
          width: 13%;
        }

        .table th:nth-child(3),
        .table td:nth-child(3) {
          width: 21%;
        }

        .table th:nth-child(4),
        .table td:nth-child(4) {
          width: 23%;
        }

        .right {
          text-align: right;
        }

        /* ==================================================
           TOTAL
        ================================================== */

        .total {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 14px;
          font-weight: 700;
          margin-top: 4px;
        }

        /* ==================================================
           PAIEMENT
        ================================================== */

        .payment {
          margin-top: 7px;
          font-size: 9px;
        }

        /* ==================================================
           OBSERVATION
        ================================================== */

        .observation {
          margin-top: 6px;
          font-size: 8px;
          line-height: 1.4;
        }

        /* ==================================================
           QR CODE
        ================================================== */

        .qr-section {
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          margin-top: 10px;
        }

        .qr-code {
          width: 26mm;
          height: 26mm;
          display: block;
        }

        .qr-text {
          margin-top: 2px;
          font-size: 7px;
          text-align: right;
        }

        /* ==================================================
           FOOTER
        ================================================== */

        .footer {
          border-top: 1px dashed #000;
          margin-top: 9px;
          padding-top: 6px;
          text-align: center;
          font-size: 7px;
          line-height: 1.5;
        }

        /* ==================================================
           ÉCRAN
        ================================================== */

        .print-loading,
        .print-error {
          width: 100%;
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: Arial, sans-serif;
        }

        /* ==================================================
           IMPRESSION
        ================================================== */

        @media print {

          html,
          body {
            width: 80mm !important;
            margin: 0 !important;
            padding: 0 !important;
            background: #fff !important;
          }

          .print-page {
            width: 80mm !important;
            margin: 0 !important;
            padding: 0 !important;
          }

          .receipt {
            width: 72mm !important;
            margin: 0 auto !important;
            box-shadow: none !important;
          }

        }

      `}</style>
    </>
  );
}

export default BlanchisseriePrint;